import {
  EXPENSE_CATEGORY_LABELS,
  formatARS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  type CashCloseDto,
  type CashDayDto,
  type PaymentMethodTotals,
} from '@gl/shared';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, ChevronRight, Lock, ReceiptText } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/FullScreenLoader';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '@/lib/api';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { cn } from '@/lib/utils';
import { formatBusinessDate, formatSignedARS, formatTime } from './format';
import { MoneyInput } from './MoneyInput';
import { caja, cajaKeys, useCashToday, useInvalidateCaja } from './queries';
import { SaleLines, SaleStatusBadge } from './SaleDetail';

const PAGE_SIZE = 20;

function diffClass(cents: number) {
  if (cents === 0) return 'text-success';
  return cents < 0 ? 'text-destructive' : 'text-amber-600 dark:text-amber-400';
}

function Row({ label, value, strong, className }: { label: string; value: string; strong?: boolean; className?: string }) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3 py-1 text-sm', strong && 'font-semibold', className)}>
      <span className={strong ? undefined : 'text-muted-foreground'}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function ByMethod({ totals }: { totals: PaymentMethodTotals }) {
  const rows = PAYMENT_METHODS.filter((m) => totals[m]);
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">Sin ventas.</p>;
  return (
    <div>
      {rows.map((m) => (
        <Row key={m} label={PAYMENT_METHOD_LABELS[m]} value={formatARS(totals[m] ?? 0)} />
      ))}
    </div>
  );
}

function CloseForm({ today }: { today: CashDayDto }) {
  const local = useCurrentLocal();
  const invalidate = useInvalidateCaja(local.id);
  const [opening, setOpening] = useState<number | null>(today.lastClose?.countedCashCents ?? null);
  const [counted, setCounted] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const expected = (opening ?? 0) + today.cashExpectedCents;
  const difference = counted === null ? null : counted - expected;

  const close = useMutation({
    mutationFn: () =>
      caja.close(local.id, {
        businessDate: today.businessDate,
        openingCashCents: opening ?? 0,
        countedCashCents: counted!,
        note: note.trim() || null,
      }),
    onSuccess: (c) => {
      toast.success(`Caja del ${formatBusinessDate(c.businessDate)} cerrada`);
      void invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cerrar la caja de hoy</CardTitle>
        <CardDescription>
          Contá el efectivo del cajón. Al cerrar, las ventas, gastos e ingresos del día quedan fijos.
        </CardDescription>
      </CardHeader>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (counted === null) return;
          const msg =
            difference === 0
              ? '¿Cerrar la caja? Cuadra justo.'
              : `¿Cerrar la caja con una diferencia de ${formatSignedARS(difference ?? 0)}?`;
          if (window.confirm(msg)) close.mutate();
        }}
      >
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="opening">Efectivo inicial</Label>
            <MoneyInput id="opening" value={opening} onChange={setOpening} placeholder="0" className="h-12 text-lg" />
            <p className="text-xs text-muted-foreground">Lo que había en el cajón al abrir.</p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="counted">Efectivo contado</Label>
            <MoneyInput id="counted" value={counted} onChange={setCounted} placeholder="0" className="h-12 text-lg" />
            <p className="text-xs text-muted-foreground">Lo que hay ahora.</p>
          </div>
          <div className="rounded-xl bg-muted/60 p-4 sm:col-span-2">
            <Row label="Efectivo inicial" value={formatARS(opening ?? 0)} />
            <Row label="+ Ventas en efectivo" value={formatARS(today.cashSalesCents)} />
            <Row label="+ Ingresos en efectivo" value={formatARS(today.cashIncomesCents)} />
            <Row label="− Gastos en efectivo" value={formatARS(today.cashExpensesCents)} />
            <Row label="Esperado" value={formatARS(expected)} strong className="mt-1 border-t pt-2" />
            <Row label="Contado" value={counted === null ? '—' : formatARS(counted)} />
            <Row
              label="Diferencia"
              value={difference === null ? '—' : formatSignedARS(difference)}
              strong
              className={difference === null ? undefined : diffClass(difference)}
            />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label htmlFor="close-note">Nota (opcional)</Label>
            <Textarea id="close-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </CardContent>
        <CardFooter className="mt-5 justify-end">
          <Button type="submit" size="lg" disabled={counted === null || close.isPending}>
            {close.isPending && <Spinner />} Cerrar caja
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function TodayStatus({ today }: { today: CashDayDto }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Hoy · {formatBusinessDate(today.businessDate)}
          {today.isClosed ? (
            <Badge variant="secondary">
              <Lock className="size-3" /> Cerrada
            </Badge>
          ) : (
            <Badge variant="success">Abierta</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div>
          <Row label="Ventas" value={`${today.salesCount}`} />
          <Row label="Total vendido" value={formatARS(today.salesTotalCents)} strong />
          {today.voidedCount > 0 && <Row label="Anuladas" value={`${today.voidedCount}`} />}
          <Row label="Gastos" value={formatARS(today.expensesTotalCents)} />
          <Row label="Ingresos" value={formatARS(today.incomesTotalCents)} />
        </div>
        <div>
          <p className="mb-1 text-xs font-medium text-muted-foreground uppercase">Por método de pago</p>
          <ByMethod totals={today.byPaymentMethod} />
        </div>
        {today.isClosed && today.lastClose && (
          <div className="flex items-center gap-2 rounded-xl bg-muted/60 p-3 text-sm sm:col-span-2">
            <CheckCircle2 className="size-4 text-success" />
            Cerrada por {today.lastClose.closedBy.name}: esperado {formatARS(today.lastClose.expectedCashCents)}, contado{' '}
            {formatARS(today.lastClose.countedCashCents)}{' '}
            <span className={cn('font-semibold', diffClass(today.lastClose.differenceCents))}>
              ({formatSignedARS(today.lastClose.differenceCents)})
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CloseDetailDialog({ close, onOpenChange }: { close: CashCloseDto | null; onOpenChange: (o: boolean) => void }) {
  const local = useCurrentLocal();
  const detail = useQuery({
    queryKey: cajaKeys.close(local.id, close?.id ?? ''),
    queryFn: () => caja.getClose(local.id, close!.id),
    enabled: !!close,
  });
  const d = detail.data;
  return (
    <Dialog open={!!close} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        {close && (
          <>
            <DialogHeader>
              <DialogTitle>Cierre del {formatBusinessDate(close.businessDate, { year: true })}</DialogTitle>
              <DialogDescription>
                {close.closedBy.name} · {formatTime(close.closedAt, local.timezone)}
                {close.note && ` · ${close.note}`}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Row label="Efectivo inicial" value={formatARS(close.openingCashCents)} />
                <Row label="Esperado" value={formatARS(close.expectedCashCents)} />
                <Row label="Contado" value={formatARS(close.countedCashCents)} />
                <Row
                  label="Diferencia"
                  value={formatSignedARS(close.differenceCents)}
                  strong
                  className={diffClass(close.differenceCents)}
                />
              </div>
              <div>
                <Row label={`Ventas (${close.salesCount})`} value={formatARS(close.salesTotalCents)} strong />
                <ByMethod totals={close.byPaymentMethod} />
                <Row label="Gastos" value={formatARS(close.expensesTotalCents)} />
                <Row label="Ingresos" value={formatARS(close.incomesTotalCents)} />
              </div>
            </div>
            {detail.isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : d ? (
              <div className="grid gap-4">
                <section>
                  <h3 className="mb-1 text-sm font-semibold">Ventas</h3>
                  {d.sales.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin ventas.</p>
                  ) : (
                    <ul className="divide-y rounded-lg border">
                      {d.sales.map((s) => (
                        <li key={s.id} className="px-3 py-1">
                          <details>
                            <summary className="flex cursor-pointer list-none items-center gap-2 py-1 text-sm">
                              <span className="w-12 font-semibold text-muted-foreground tabular-nums">#{s.number}</span>
                              <span className="flex-1 text-muted-foreground">
                                {formatTime(s.soldAt, local.timezone)} · {PAYMENT_METHOD_LABELS[s.paymentMethod]}
                              </span>
                              <SaleStatusBadge sale={{ ...s, cashCloseId: null }} />
                              <span className={cn('tabular-nums', s.status === 'VOIDED' && 'line-through')}>
                                {formatARS(s.totalCents)}
                              </span>
                            </summary>
                            <SaleLines sale={s} />
                          </details>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                {(d.expenses.length > 0 || d.incomes.length > 0) && (
                  <section>
                    <h3 className="mb-1 text-sm font-semibold">Gastos e ingresos</h3>
                    <ul className="divide-y rounded-lg border text-sm">
                      {d.expenses.map((e) => (
                        <li key={e.id} className="flex justify-between gap-2 px-3 py-2">
                          <span>
                            {EXPENSE_CATEGORY_LABELS[e.category]}
                            {e.description && <span className="text-muted-foreground"> · {e.description}</span>}
                            <span className="text-muted-foreground"> · {PAYMENT_METHOD_LABELS[e.paymentMethod]}</span>
                          </span>
                          <span className="tabular-nums">−{formatARS(e.amountCents)}</span>
                        </li>
                      ))}
                      {d.incomes.map((i) => (
                        <li key={i.id} className="flex justify-between gap-2 px-3 py-2">
                          <span>
                            Ingreso
                            {i.description && <span className="text-muted-foreground"> · {i.description}</span>}
                            <span className="text-muted-foreground"> · {PAYMENT_METHOD_LABELS[i.paymentMethod]}</span>
                          </span>
                          <span className="tabular-nums">+{formatARS(i.amountCents)}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** `canCloseToday`: la caja de hoy sigue abierta y el formulario de cierre está arriba. */
function ClosesList({ canCloseToday }: { canCloseToday: boolean }) {
  const local = useCurrentLocal();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<CashCloseDto | null>(null);
  const q = { page, pageSize: PAGE_SIZE };
  const closes = useQuery({
    queryKey: cajaKeys.closes(local.id, q),
    queryFn: () => caja.listCloses(local.id, q),
    placeholderData: keepPreviousData,
  });
  const totalPages = closes.data ? Math.max(1, Math.ceil(closes.data.total / PAGE_SIZE)) : 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cierres anteriores</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        {closes.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : !closes.data?.data.length ? (
          <EmptyState
            compact
            icon={ReceiptText}
            title="Todavía no cerraste ninguna caja"
            description="Al final del día contá el efectivo y cerrá la caja: acá vas a ver cada cierre con su diferencia."
            action={
              canCloseToday && (
                <Button
                  size="sm"
                  onClick={() => {
                    const input = document.getElementById('counted');
                    input?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    input?.focus({ preventScroll: true });
                  }}
                >
                  Cerrar la caja de hoy
                </Button>
              )
            }
          />
        ) : (
          <>
            <ul className="divide-y md:hidden">
              {closes.data.data.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(c)}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-accent/50"
                  >
                    <span className="flex-1">
                      <span className="block text-sm font-medium">{formatBusinessDate(c.businessDate)}</span>
                      <span className="block text-xs text-muted-foreground">
                        {c.salesCount} ventas · {formatARS(c.salesTotalCents)}
                      </span>
                    </span>
                    <span className={cn('text-sm font-semibold tabular-nums', diffClass(c.differenceCents))}>
                      {formatSignedARS(c.differenceCents)}
                    </span>
                    <ChevronRight className="size-4 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-5">Día</TableHead>
                    <TableHead className="text-right">Ventas</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Esperado</TableHead>
                    <TableHead className="text-right">Contado</TableHead>
                    <TableHead className="pr-5 text-right">Diferencia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {closes.data.data.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelected(c)}>
                      <TableCell className="pl-5 font-medium">{formatBusinessDate(c.businessDate, { year: true })}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.salesCount}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatARS(c.salesTotalCents)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatARS(c.expectedCashCents)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatARS(c.countedCashCents)}</TableCell>
                      <TableCell className={cn('pr-5 text-right font-semibold tabular-nums', diffClass(c.differenceCents))}>
                        {formatSignedARS(c.differenceCents)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
      {totalPages > 1 && (
        <CardFooter className="justify-between">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Siguiente
          </Button>
        </CardFooter>
      )}
      <CloseDetailDialog close={selected} onOpenChange={(o) => !o && setSelected(null)} />
    </Card>
  );
}

export function CierresPage() {
  const local = useCurrentLocal();
  const canClose = useHasRole('OWNER', 'MANAGER');
  const today = useCashToday(local.id);

  return (
    <div>
      <PageHeader
        title="Cierres de caja"
        description="Arqueo del efectivo y cierre del día."
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to=".." relative="path">
              <ArrowLeft /> Caja
            </Link>
          </Button>
        }
      />
      {!canClose ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Solo el dueño o el encargado pueden cerrar la caja.
          </CardContent>
        </Card>
      ) : today.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="size-6" />
        </div>
      ) : today.isError || !today.data ? (
        <p className="text-sm text-destructive">{errorMessage(today.error)}</p>
      ) : (
        <div className="grid gap-6">
          <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
            <TodayStatus today={today.data} />
            {!today.data.isClosed && <CloseForm key={today.data.businessDate} today={today.data} />}
          </div>
          <ClosesList canCloseToday={!today.data.isClosed} />
        </div>
      )}
    </div>
  );
}
